import { CloudOutlined } from '@ant-design/icons';
import { SelectOption } from 'components/common/input-components/select';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Select from 'components/common/input-components/select';
import { useGetAgents } from 'stores/rq/agents';
import styled from '@emotion/styled';
import { DEFAULT_AGENT_ID } from 'constants/agents.constants';
import { getAgentDisplayName, isSaasDefaultAgent } from 'utils/agent.utils';

export type AgentsSelectionType = {
  shouldDisplaySaasAgent?: boolean;
  shouldDisplayDefaultAgent?: boolean;
  onChange?: (value: string) => void;
  value?: string;
  'data-e2e': string;
  disabled?: boolean;
};

export interface AgentDetails {
  name?: string;
  agentKey: string;
  isSaas?: boolean;
}

const DEFAULT_AGENT_PLACEHOLDER = { agentKey: DEFAULT_AGENT_ID, name: `Default Agent` };

const AgentsSelection: React.FC<AgentsSelectionType> = ({
  shouldDisplaySaasAgent = true,
  shouldDisplayDefaultAgent = true,
  onChange,
  value,
  'data-e2e': dataE2e,
  disabled
}) => {
  const { data: agents, isLoading } = useGetAgents(true);
  const intl = useIntl();

  // The default resolves to the env0 cloud agent, which cannot serve a self-hosted VCS. It stays on offer
  // for a connection already saved with it, so editing one does not open on an empty field.
  const isDefaultAgentSelectable = shouldDisplaySaasAgent || !isSaasDefaultAgent(agents) || value === DEFAULT_AGENT_ID;

  const agentsToSelect: AgentDetails[] = useMemo(() => {
    if (!agents) return [DEFAULT_AGENT_PLACEHOLDER];

    const defaultAgent = agents.find(agent => agent.isDefault);

    return [
      ...(shouldDisplayDefaultAgent && isDefaultAgentSelectable
        ? [
            {
              agentKey: DEFAULT_AGENT_ID,
              name: intl.formatMessage(
                { id: 'organization.settings.agents.agent-assignment.default-agent-option' },
                { agentDisplayName: getAgentDisplayName(defaultAgent) }
              )
            }
          ]
        : []),
      ...(shouldDisplaySaasAgent ? agents : agents.filter(agent => !agent.isSaas))
    ];
  }, [agents, intl, isDefaultAgentSelectable, shouldDisplayDefaultAgent, shouldDisplaySaasAgent]);

  return (
    <Select
      data-e2e={dataE2e}
      // Select swaps every option for a loading one while the list is in flight, and antd prints the raw
      // value when no option carries it. Show the placeholder instead of a bare agent key.
      value={isLoading ? undefined : value}
      onChange={onChange}
      disabled={disabled}
      loading={isLoading}
      placeholder={intl.formatMessage({ id: 'organization.settings.agents.agent-assignment.select-agent' })}>
      {agentsToSelect.map((agent: AgentDetails) => (
        <SelectOption key={agent.agentKey} value={agent.agentKey} data-e2e={`agent-option-${agent.agentKey}`}>
          <StyledOption key={agent.agentKey}>
            {agent.isSaas && (
              <>
                <CloudOutlined data-e2e="saas-agent-mark" /> &nbsp;
              </>
            )}
            {getAgentDisplayName(agent)}
          </StyledOption>
        </SelectOption>
      ))}
    </Select>
  );
};

const StyledOption = styled.div`
  display: flex;
  align-items: center;
`;

export default AgentsSelection;
